<template>
  <div class="product-list">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>商品管理</span>
          <div>
            <el-button type="success" @click="batchDialogVisible = true">批量导入</el-button>
            <el-button type="primary" @click="handleAdd">添加商品</el-button>
          </div>
        </div>
      </template>
      
      <el-table :data="productList" style="width: 100%" v-loading="loading">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column label="图片" width="100">
          <template #default="{ row }">
            <el-image :src="row.image_url" style="width: 60px; height: 60px" fit="cover" />
          </template>
        </el-table-column>
        <el-table-column prop="name" label="商品名称" />
        <el-table-column prop="category_name" label="分类" />
        <el-table-column prop="price" label="价格">
          <template #default="{ row }">
            ¥{{ row.price }}
          </template>
        </el-table-column>
        <el-table-column prop="stock" label="库存" />
        <el-table-column prop="status" label="状态">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'">
              {{ row.status === 1 ? '上架' : '下架' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleEdit(row)">编辑</el-button>
            <el-button type="danger" size="small" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <el-pagination
        v-model:current-page="page"
        v-model:page-size="pageSize"
        :total="total"
        layout="total, prev, pager, next"
        style="margin-top: 20px"
        @current-change="handlePageChange"
      />
    </el-card>
    
    <!-- 编辑/添加对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="600px">
      <el-form :model="form" label-width="100px">
        <el-form-item label="商品名称">
          <el-input v-model="form.name" />
        </el-form-item>
        <el-form-item label="分类">
          <el-select v-model="form.category_id">
            <el-option 
              v-for="item in categories" 
              :key="item.id" 
              :label="item.name" 
              :value="item.id" 
            />
          </el-select>
        </el-form-item>
        <el-form-item label="价格">
          <el-input-number v-model="form.price" :min="0" :precision="2" />
        </el-form-item>
        <el-form-item label="库存">
          <el-input-number v-model="form.stock" :min="0" />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch 
            v-model="form.status" 
            :active-value="1" 
            :inactive-value="0"
            active-text="上架"
            inactive-text="下架"
          />
        </el-form-item>
        <el-form-item label="商品图片">
          <el-upload
            class="product-uploader"
            :http-request="customUpload"
            :show-file-list="false"
            :before-upload="beforeUpload"
            accept="image/*"
          >
            <img v-if="form.image_url" :src="form.image_url" class="product-image" />
            <el-icon v-else class="uploader-icon"><Plus /></el-icon>
          </el-upload>
          <el-input v-model="form.image_url" placeholder="或输入图片URL" style="margin-top: 10px" />
        </el-form-item>
        <el-form-item label="商品描述">
          <el-input v-model="form.description" type="textarea" :rows="3" />
        </el-form-item>
        <el-form-item label="营销标签">
          <el-checkbox-group v-model="form.tagsArray">
            <el-checkbox value="seckill" label="秒杀"></el-checkbox>
            <el-checkbox value="newbie" label="新人专享"></el-checkbox>
            <el-checkbox value="discount" label="折扣"></el-checkbox>
          </el-checkbox-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSave">保存</el-button>
      </template>
    </el-dialog>

    <!-- 批量导入对话框 -->
    <el-dialog v-model="batchDialogVisible" title="批量导入商品" width="800px">
      <el-tabs v-model="batchActiveTab">
        <el-tab-pane label="JSON导入" name="json">
          <el-form label-width="100px">
            <el-form-item label="商品数据">
              <el-input 
                v-model="batchJson" 
                type="textarea" 
                :rows="10" 
                placeholder='[{&quot;name&quot;:&quot;商品1&quot;,&quot;price&quot;:9.99,&quot;stock&quot;:100,&quot;category_id&quot;:1}]'
              />
            </el-form-item>
          </el-form>
        </el-tab-pane>
        <el-tab-pane label="CSV导入" name="csv">
          <el-form label-width="100px">
            <el-form-item label="CSV数据">
              <el-input 
                v-model="batchCsv" 
                type="textarea" 
                :rows="10" 
                placeholder="name,price,stock,category_id,description,image_url&#10;商品1,9.99,100,1,描述,http://example.com/1.jpg"
              />
            </el-form-item>
            <el-form-item>
              <el-button type="info" size="small" @click="downloadTemplate">
                <el-icon><Download /></el-icon> 下载CSV模板
              </el-button>
            </el-form-item>
            <el-form-item>
              <el-alert 
                title="CSV格式说明" 
                type="info" 
                :closable="false"
                description="第一行为表头：name,price,stock,category_id,description,image_url。每行一个商品，用逗号分隔。"
              />
            </el-form-item>
          </el-form>
        </el-tab-pane>
      </el-tabs>
      
      <!-- 导入结果 -->
      <div v-if="batchResult" class="batch-result">
        <el-divider />
        <el-alert 
          :title="`导入完成：成功 ${batchResult.success} 条，失败 ${batchResult.failed} 条`" 
          :type="batchResult.failed === 0 ? 'success' : 'warning'"
          :closable="false"
        />
        <el-table v-if="batchResult.errors && batchResult.errors.length > 0" :data="batchResult.errors" size="small" style="margin-top: 10px">
          <el-table-column prop="index" label="行号" width="80" />
          <el-table-column prop="error" label="错误信息" />
        </el-table>
      </div>
      
      <template #footer>
        <el-button @click="batchDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleBatchImport" :loading="batchLoading">开始导入</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Download } from '@element-plus/icons-vue'
import request from '../../utils/request'

export default {
  name: 'ProductList',
  setup() {
    const productList = ref([])
    const categories = ref([])
    const loading = ref(false)
    const page = ref(1)
    const pageSize = ref(10)
    const total = ref(0)
    const dialogVisible = ref(false)
    const dialogTitle = ref('')
    const form = ref({})
    
    // 批量导入相关
    const batchDialogVisible = ref(false)
    const batchActiveTab = ref('json')
    const batchJson = ref('')
    const batchCsv = ref('')
    const batchLoading = ref(false)
    const batchResult = ref(null)
    
    const fetchProducts = async () => {
      loading.value = true
      try {
        const res = await request.get('/api/admin/products', {
          params: { page: page.value, limit: pageSize.value }
        })
        productList.value = res.data || []
        total.value = res.total || 0
      } catch (error) {
        ElMessage.error('获取商品失败')
      } finally {
        loading.value = false
      }
    }
    
    const fetchCategories = async () => {
      try {
        const res = await request.get('/api/admin/shop-categories')
        categories.value = res.data || []
      } catch (error) {
        console.error('获取分类失败')
      }
    }
    
    const handleAdd = () => {
      form.value = { status: 1, stock: 0, price: 0, tagsArray: [] }
      dialogTitle.value = '添加商品'
      dialogVisible.value = true
    }
    
    const handleEdit = (row) => {
      var parsedTags = []
      try {
        parsedTags = typeof row.tags === 'string' ? JSON.parse(row.tags) : (Array.isArray(row.tags) ? row.tags : [])
      } catch(e) { parsedTags = [] }
      form.value = { ...row, tagsArray: parsedTags }
      dialogTitle.value = '编辑商品'
      dialogVisible.value = true
    }
    
    // 图片上传相关
    const customUpload = async (options) => {
      const formData = new FormData()
      formData.append('file', options.file)
      
      try {
        const res = await request.post('/api/admin/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        if (res.code === 0 && res.data && res.data.url) {
          form.value.image_url = res.data.url
          ElMessage.success('上传成功')
        } else {
          ElMessage.error(res.message || '上传失败')
        }
      } catch (error) {
        ElMessage.error('上传失败：' + (error.message || '网络错误'))
      }
    }
    
    const beforeUpload = (file) => {
      const isImage = file.type.startsWith('image/')
      const isLt2M = file.size / 1024 / 1024 < 2
      
      if (!isImage) {
        ElMessage.error('只能上传图片文件')
        return false
      }
      if (!isLt2M) {
        ElMessage.error('图片大小不能超过2MB')
        return false
      }
      return true
    }
    
    const handleSave = async () => {
      try {
        // 后端字段是image，前端表单用image_url展示，保存时映射
        // tags: 前端tagsArray数组→后端tags JSON字符串
        var submitTags = Array.isArray(form.value.tagsArray) ? JSON.stringify(form.value.tagsArray) : '[]'
        const submitData = { ...form.value, image: form.value.image_url || form.value.image || '', tags: submitTags }
        delete submitData.tagsArray
        await request.post('/api/admin/products', submitData)
        ElMessage.success('保存成功')
        dialogVisible.value = false
        fetchProducts()
      } catch (error) {
        ElMessage.error('保存失败')
      }
    }
    
    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm('确认删除该商品？', '提示', { type: 'warning' })
        await request.delete(`/api/admin/products/${row.id}`)
        ElMessage.success('删除成功')
        fetchProducts()
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除失败')
        }
      }
    }
    
    const handlePageChange = (val) => {
      page.value = val
      fetchProducts()
    }
    
    // 下载CSV模板
    const downloadTemplate = () => {
      const template = 'name,price,stock,category_id,description,image_url\n商品名称,9.99,100,1,商品描述,https://example.com/image.jpg\n示例商品,19.99,50,2,示例描述,https://example.com/image2.jpg'
      const blob = new Blob(['\ufeff' + template], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = '商品导入模板.csv'
      link.click()
      URL.revokeObjectURL(link.href)
      ElMessage.success('模板下载成功')
    }
    
    // 批量导入
    const handleBatchImport = async () => {
      batchLoading.value = true
      batchResult.value = null
      
      try {
        let res
        if (batchActiveTab.value === 'json') {
          if (!batchJson.value.trim()) {
            ElMessage.warning('请输入JSON数据')
            return
          }
          const products = JSON.parse(batchJson.value)
          res = await request.post('/api/admin/products/batch', { products })
        } else {
          if (!batchCsv.value.trim()) {
            ElMessage.warning('请输入CSV数据')
            return
          }
          res = await request.post('/api/admin/products/batch-csv', { csv: batchCsv.value })
        }
        
        batchResult.value = res
        if (res.failed === 0) {
          ElMessage.success(`成功导入 ${res.success} 条数据`)
          fetchProducts()
        } else {
          ElMessage.warning(`导入完成，成功 ${res.success} 条，失败 ${res.failed} 条`)
        }
      } catch (error) {
        if (error instanceof SyntaxError) {
          ElMessage.error('JSON格式错误，请检查输入')
        } else {
          ElMessage.error('导入失败：' + (error.message || '未知错误'))
        }
      } finally {
        batchLoading.value = false
      }
    }
    
    onMounted(() => {
      fetchProducts()
      fetchCategories()
    })
    
    return {
      productList,
      categories,
      loading,
      page,
      pageSize,
      total,
      dialogVisible,
      dialogTitle,
      form,
      batchDialogVisible,
      batchActiveTab,
      batchJson,
      batchCsv,
      batchLoading,
      batchResult,
      handleAdd,
      handleEdit,
      handleSave,
      handleDelete,
      handlePageChange,
      customUpload,
      beforeUpload,
      handleBatchImport,
      downloadTemplate
    }
  }
}
</script>

<style scoped>
.product-list {
  padding: 8px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}

.card-header span {
  font-weight: 600;
  font-size: 16px;
  color: #303133;
}

.batch-result {
  margin-top: 20px;
}

.product-uploader {
  border: 2px dashed #d9d9d9;
  border-radius: 12px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  width: 120px;
  height: 120px;
  background: #fafbfc;
}

.product-uploader:hover {
  border-color: #409EFF;
  background: rgba(64, 158, 255, 0.02);
}

.uploader-icon {
  font-size: 28px;
  color: #c0c4cc;
  width: 120px;
  height: 120px;
  text-align: center;
  line-height: 120px;
  transition: all 0.3s ease;
}

.product-uploader:hover .uploader-icon {
  color: #409EFF;
}

.product-image {
  width: 120px;
  height: 120px;
  display: block;
  object-fit: cover;
  border-radius: 12px;
}

/* 表格商品图片 */
:deep(.el-image) {
  border-radius: 8px;
  overflow: hidden;
}
</style>